import { useState, useEffect, useRef } from 'react';
import { 
  Shield, Key, Lock, Unlock, Download, Upload, Trash2, 
  Clock, Check, X, RefreshCw, Smartphone, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

export default function SecuritySettings({ primaryColor }) {
  const [loading, setLoading] = useState(false);
  const [twoFAStatus, setTwoFAStatus] = useState({ enabled: false, method: null, contact: null });
  const [providerStatus, setProviderStatus] = useState({ emailAvailable: null, smsAvailable: null });
  const [backups, setBackups] = useState([]);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePassword, setRestorePassword] = useState('');
  const restoreFileRef = useRef(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [setupMethod, setSetupMethod] = useState('email');
  const [setupContact, setSetupContact] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [showContactInput, setShowContactInput] = useState(false);
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch2FAStatus();
    fetch2FAProviders();
    fetchBackups();
  }, []);

  const fetch2FAStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/2fa/status`, { headers });
      setTwoFAStatus(response.data);
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
    }
  };

  const fetch2FAProviders = async () => {
    try {
      const response = await axios.get(`${API}/auth/2fa/providers`, { headers });
      setProviderStatus({
        emailAvailable: Boolean(response.data.email_available),
        smsAvailable: Boolean(response.data.sms_available)
      });
    } catch (error) {
      console.error('Error fetching 2FA providers:', error);
      setProviderStatus({ emailAvailable: null, smsAvailable: null });
    }
  };

  const fetchBackups = async () => {
    try {
      const response = await axios.get(`${API}/admin/backups`, { headers });
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error fetching backups:', error);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, { headers });
      
      toast.success('Password cambiata con successo!');
      setShowPasswordForm(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel cambio password');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async (method) => {
    if (method === 'email' && providerStatus.emailAvailable === false) {
      toast.error('Email 2FA non configurata: aggiungi RESEND_API_KEY e SENDER_EMAIL su Render');
      return;
    }
    if (method === 'sms' && providerStatus.smsAvailable === false) {
      toast.error('SMS 2FA non configurato: aggiungi le variabili Twilio su Render');
      return;
    }
    setSetupMethod(method);
    setSetupContact('');
    setShowContactInput(true);
  };

  const handleSendCode = async () => {
    if (!setupContact.trim()) {
      toast.error(setupMethod === 'email' ? 'Inserisci un\'email' : 'Inserisci un numero di telefono');
      return;
    }

    if (setupMethod === 'email' && !setupContact.includes('@')) {
      toast.error('Inserisci un\'email valida');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/enable`, { 
        method: setupMethod,
        contact: setupContact
      }, { headers });
      
      if (response.data.debug_code) {
        setSetupCode(response.data.debug_code);
      }
      setShowContactInput(false);
      setShow2FASetup(true);
      toast.info(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'attivazione 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Inserisci un codice di 6 cifre');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/verify-setup`, { code: verifyCode }, { headers });
      toast.success('Autenticazione a due fattori attivata!');
      setShow2FASetup(false);
      setVerifyCode('');
      fetch2FAStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codice non valido');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Sei sicuro di voler disattivare l\'autenticazione a due fattori?')) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, {}, { headers });
      toast.success('2FA disattivato');
      fetch2FAStatus();
    } catch (error) {
      toast.error('Errore nella disattivazione');
    } finally {
      setLoading(false);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleCreateBackup = async () => {
    setLoading(true);
    try {
      const created = await axios.post(`${API}/admin/backup`, {}, { headers });
      const filename = created.data.filename;
      const response = await axios.get(`${API}/admin/backup/download/${encodeURIComponent(filename)}`, {
        headers,
        responseType: 'blob'
      });
      downloadBlob(response.data, filename);
      toast.success('Backup completo creato e scaricato sul Mac');
      fetchBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel backup');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBackup = async (filename) => {
    try {
      const response = await axios.get(`${API}/admin/backup/download/${encodeURIComponent(filename)}`, {
        headers,
        responseType: 'blob'
      });
      downloadBlob(response.data, filename);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il download');
    }
  };

  const requireRestorePassword = () => {
    if (!restorePassword) {
      toast.error('Inserisci la password amministratore per ripristinare');
      return false;
    }
    return true;
  };

  const handleRestoreBackup = async (filename) => {
    if (!requireRestorePassword()) return;
    if (!window.confirm(`Ripristinare ${filename}? I dati attuali verranno sostituiti, ma prima sarà creato un backup di sicurezza.`)) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/restore/${encodeURIComponent(filename)}`,
        { password: restorePassword },
        { headers }
      );
      toast.success(response.data.message);
      setRestorePassword('');
      setTimeout(() => window.location.reload(), 1200);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel ripristino');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreUploadedBackup = async () => {
    if (!restoreFile) {
      toast.error('Seleziona prima un file backup JSON');
      return;
    }
    if (!requireRestorePassword()) return;
    if (!window.confirm(`Ripristinare il file ${restoreFile.name}? Prima verrà creato un backup di sicurezza dello stato attuale.`)) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', restoreFile);
      formData.append('password', restorePassword);
      const response = await axios.post(`${API}/admin/backup/restore-upload`, formData, { headers });
      toast.success(response.data.message);
      setRestoreFile(null);
      setRestorePassword('');
      if (restoreFileRef.current) restoreFileRef.current.value = '';
      setTimeout(() => window.location.reload(), 1200);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel ripristino del file');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Change Password Section */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
              <Key className="w-5 h-5" style={{ color: primaryColor }} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Cambia Password</h3>
              <p className="text-sm text-gray-500">Modifica la password del tuo account</p>
            </div>
          </div>
          <button
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            className="px-4 py-2 rounded-lg font-medium text-sm transition-colors"
            style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
          >
            {showPasswordForm ? 'Annulla' : 'Modifica'}
          </button>
        </div>
        
        {showPasswordForm && (
          <form onSubmit={handlePasswordChange} className="space-y-4 pt-4 border-t">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password Attuale</label>
              <input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': primaryColor }}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nuova Password</label>
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': primaryColor }}
                required
                minLength={6}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Conferma Nuova Password</label>
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': primaryColor }}
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg text-white font-semibold disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {loading ? 'Salvataggio...' : 'Salva Nuova Password'}
            </button>
          </form>
        )}
      </div>

      {/* Two-Factor Authentication Section */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: twoFAStatus.enabled ? '#dcfce7' : `${primaryColor}15` }}>
              <Shield className="w-5 h-5" style={{ color: twoFAStatus.enabled ? '#16a34a' : primaryColor }} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Autenticazione a Due Fattori (2FA)</h3>
              <p className="text-sm text-gray-500">
                {twoFAStatus.enabled 
                  ? `Attiva via ${twoFAStatus.method === 'email' ? 'Email' : 'SMS'}: ${twoFAStatus.contact || ''}` 
                  : 'Aggiungi un livello di sicurezza extra'}
              </p>
            </div>
          </div>
          {twoFAStatus.enabled ? (
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center gap-1">
              <Check className="w-4 h-4" /> Attivo
            </span>
          ) : (
            <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium flex items-center gap-1">
              <X className="w-4 h-4" /> Non attivo
            </span>
          )}
        </div>

        {!twoFAStatus.enabled && !show2FASetup && !showContactInput && (
          <div className="pt-4 border-t space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => handleEnable2FA('email')}
                disabled={loading || providerStatus.emailAvailable === false}
                className="flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed hover:border-solid transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderColor: primaryColor, color: primaryColor }}
              >
                <Mail className="w-5 h-5" />
                <span className="font-medium">
                  {providerStatus.emailAvailable === false ? 'Email da configurare' : 'Attiva via Email'}
                </span>
              </button>
              <button
                onClick={() => handleEnable2FA('sms')}
                disabled={loading || providerStatus.smsAvailable === false}
                className="flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed hover:border-solid transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderColor: primaryColor, color: primaryColor }}
              >
                <Smartphone className="w-5 h-5" />
                <span className="font-medium">
                  {providerStatus.smsAvailable === false ? 'SMS da configurare' : 'Attiva via SMS'}
                </span>
              </button>
            </div>
            {(providerStatus.emailAvailable === false || providerStatus.smsAvailable === false) && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                Il codice è pronto, ma l'invio richiede un provider configurato su Render: Resend per le email e Twilio per gli SMS.
              </p>
            )}
          </div>
        )}

        {/* Contact Input Step */}
        {showContactInput && (
          <div className="pt-4 border-t space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-blue-700 text-sm flex items-center gap-2">
                {setupMethod === 'email' ? <Mail className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
                Inserisci {setupMethod === 'email' ? "l'email" : 'il numero di telefono'} dove vuoi ricevere i codici di verifica
              </p>
            </div>
            <input
              type={setupMethod === 'email' ? 'email' : 'tel'}
              value={setupContact}
              onChange={(e) => setSetupContact(e.target.value)}
              placeholder={setupMethod === 'email' ? 'esempio@email.com' : '+39 333 1234567'}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': primaryColor }}
            />
            <div className="flex gap-2">
              <button
                onClick={() => { setShowContactInput(false); setSetupContact(''); }}
                className="flex-1 py-3 rounded-lg border font-medium"
              >
                Annulla
              </button>
              <button
                onClick={handleSendCode}
                disabled={loading || !setupContact.trim()}
                className="flex-1 py-3 rounded-lg text-white font-semibold disabled:opacity-50"
                style={{ backgroundColor: primaryColor }}
              >
                {loading ? 'Invio...' : 'Invia Codice'}
              </button>
            </div>
          </div>
        )}

        {show2FASetup && (
          <div className="pt-4 border-t space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-blue-700 text-sm">
                Abbiamo inviato un codice di verifica a <strong>{setupContact}</strong>. 
                Inseriscilo qui sotto per attivare il 2FA.
              </p>
              {setupCode && (
                <p className="text-blue-900 font-mono font-bold mt-2 bg-blue-100 p-2 rounded text-center">
                  Codice (modalità test): {setupCode}
                </p>
              )}
            </div>
            <input
              type="text"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Inserisci codice a 6 cifre"
              className="w-full px-4 py-3 border rounded-lg text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': primaryColor }}
              maxLength={6}
            />
            <div className="flex gap-2">
              <button
                onClick={() => { 
                  setShow2FASetup(false); 
                  setVerifyCode(''); 
                  setSetupCode('');
                  setSetupContact('');
                }}
                className="flex-1 py-3 rounded-lg border font-medium"
              >
                Annulla
              </button>
              <button
                onClick={handleVerify2FA}
                disabled={loading || verifyCode.length !== 6}
                className="flex-1 py-3 rounded-lg text-white font-semibold disabled:opacity-50"
                style={{ backgroundColor: primaryColor }}
              >
                Verifica e Attiva
              </button>
            </div>
          </div>
        )}

        {twoFAStatus.enabled && (
          <div className="pt-4 border-t">
            <button
              onClick={handleDisable2FA}
              disabled={loading}
              className="text-red-600 hover:text-red-700 font-medium text-sm flex items-center gap-2"
            >
              <Unlock className="w-4 h-4" />
              Disattiva 2FA
            </button>
          </div>
        )}
      </div>

      {/* Backup Section */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
              <Download className="w-5 h-5" style={{ color: primaryColor }} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Backup Database</h3>
              <p className="text-sm text-gray-500">Salva clienti, colori, grafiche, ordini, punti e tutte le impostazioni</p>
            </div>
          </div>
          <button
            onClick={handleCreateBackup}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-white font-medium text-sm flex items-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: primaryColor }}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Crea e Scarica Backup
          </button>
        </div>
        
        <div className="bg-green-50 p-3 rounded-lg mb-4">
          <p className="text-green-700 text-sm flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Il file scaricato contiene tutte le collezioni e può essere ricaricato direttamente qui.
          </p>
        </div>

        <div className="border border-blue-200 bg-blue-50 rounded-xl p-4 mb-5 space-y-3">
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600" />
            <h4 className="font-semibold text-blue-900">Ripristina da un file salvato sul Mac</h4>
          </div>
          <input
            ref={restoreFileRef}
            type="file"
            accept="application/json,.json"
            onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-gray-700 file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border-0 file:bg-blue-100 file:text-blue-700 file:font-medium"
          />
          {restoreFile && <p className="text-xs text-blue-700">File selezionato: {restoreFile.name}</p>}
          <input
            type="password"
            value={restorePassword}
            onChange={(e) => setRestorePassword(e.target.value)}
            placeholder="Password amministratore"
            autoComplete="current-password"
            className="w-full px-4 py-3 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <button
            onClick={handleRestoreUploadedBackup}
            disabled={loading || !restoreFile || !restorePassword}
            className="w-full py-3 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50 hover:bg-blue-700"
          >
            {loading ? 'Operazione in corso...' : 'Ripristina Backup Caricato'}
          </button>
          <p className="text-xs text-blue-700">
            Il ripristino richiede la password e crea automaticamente una copia di sicurezza prima di sostituire i dati.
          </p>
        </div>

        {backups.length > 0 ? (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700">Backup Disponibili sul server</h4>
            <p className="text-xs text-gray-500">Per ripristinarli inserisci prima la password nel campo qui sopra.</p>
            {backups.map((backup) => (
              <div key={backup.filename} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Download className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="font-medium text-sm text-gray-900">{backup.filename}</p>
                    <p className="text-xs text-gray-500">{backup.size_kb} KB</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDownloadBackup(backup.filename)}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Scarica"
                  >
                    <Download className="w-4 h-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => handleRestoreBackup(backup.filename)}
                    className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                    title="Ripristina"
                  >
                    <Upload className="w-4 h-4 text-blue-600" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm text-center py-4">
            Nessun backup disponibile. Crea il primo backup!
          </p>
        )}
      </div>
    </div>
  );
}
